import { useCallback, useState } from 'react';
import { TabController } from '@ayocore/platform/client/tabs/TabController';

export interface UseUrlTabStateOptions {
  /**
   * Включено ли сохранение в URL. Когда false — хук ведёт себя как обычный useState.
   * Используется, например, чтобы не засорять URL списка, когда View открыт в модалке.
   */
  enabled?: boolean;
}

/**
 * Синхронизирует активную вкладку внутри View с search-параметром URL.
 *
 * Поведение повторяет {@link RepositoryController} для фильтров/пагинации:
 * пишет напрямую в `window.location.search` через `history.replaceState`
 * и синхронизирует URL текущей аппной вкладки через {@link TabController.setUrlAndActivate},
 * чтобы после переключения табов приложения состояние восстановилось из URL.
 *
 * Если значение совпадает с `defaultValue`, параметр из URL удаляется (чистый URL при «основной» вкладке).
 *
 * @param paramKey уникальный ключ параметра в URL (например, `deal_tab`)
 * @param defaultValue значение по умолчанию (обычно `'main'`)
 * @param options конфигурация (`enabled` — отключает запись в URL, например для модалок)
 */
export function useUrlTabState(
  paramKey: string,
  defaultValue: string,
  options: UseUrlTabStateOptions = {}
): [string, (next: string) => void] {
  const { enabled = true } = options;

  const [value, setValue] = useState<string>(() => {
    if (!enabled) return defaultValue;
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get(paramKey) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const write = useCallback(
    (next: string) => {
      setValue(next);
      if (!enabled) return;
      try {
        const sp = new URLSearchParams(window.location.search);
        if (!next || next === defaultValue) {
          sp.delete(paramKey);
        } else {
          sp.set(paramKey, next);
        }
        const search = sp.toString();
        const newUrl = `${window.location.pathname}${search ? `?${search}` : ''}`;
        window.history.replaceState(null, '', newUrl);
        queueMicrotask(() => {
          try {
            const key = window.location.pathname;
            TabController.getInstance().setUrlAndActivate(key, newUrl);
          } catch {
            /* noop */
          }
        });
      } catch {
        /* noop */
      }
    },
    [enabled, paramKey, defaultValue]
  );

  return [value, write];
}

export default useUrlTabState;
